import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getFollowing, getFollowers, followUser, unfollowUser } from "../../services/FollowerServices";
import styles from "./styles/FollowingPage.module.css";
import AuthContext from '../../context/AuthContext';

const PAGE_SIZE = 20;

const FollowingPage = () => {
    const [rows, setRows] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [loadingUserId, setLoadingUserId] = useState(null);
    const location = useLocation();
    const isFollowersPage = location.pathname.startsWith("/followers");

    const { profileCreatorId } = useParams(); // Whose profile is being viewed
    const { user: loggedInUser } = useContext(AuthContext);
    const isOwnProfile = profileCreatorId === loggedInUser?._id; // Check if the list belongs to the logged-in user

    const navigate = useNavigate();

    const fetchPage = useCallback(async (pageNum, replace) => {
        const data = isFollowersPage
            ? await getFollowers(profileCreatorId, pageNum, PAGE_SIZE)
            : await getFollowing(profileCreatorId, pageNum, PAGE_SIZE);

        setRows((prev) => (replace ? data.results : [...prev, ...data.results]));
        setHasMore(data.hasMore);
        setPage(data.page);
    }, [isFollowersPage, profileCreatorId]);

    // Toggle follow state for one row without refetching the whole list
    const handleFollowToggle = async (userId, isFollowing) => {
        try {
            setLoadingUserId(userId);

            if (isFollowing) {
                await unfollowUser(userId);
            } else {
                await followUser(userId);
            }

            setRows((prev) =>
                prev.map((row) => {
                    const rowUser = isFollowersPage ? row.follower : row.following;
                    return rowUser._id === userId
                        ? { ...row, isFollowing: !isFollowing }
                        : row;
                })
            );

        } catch (err) {
            console.error("Error updating follow status:", err);
        } finally {
            setLoadingUserId(null);
        }
    };

    const handleLoadMore = async () => {
        try {
            setIsLoadingMore(true);
            await fetchPage(page + 1, false);
        } catch (err) {
            console.error("Error loading more:", err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        let isActive = true;

        const fetchFirstPage = async () => {
            setIsLoading(true);
            setError("");

            try {
                await fetchPage(1, true);
            } catch (err) {
                console.error("Error fetching follow list:", err);

                if (!isActive) return;
                if (err.response?.status === 403) {
                    setError("This user's follower/following list is private.");
                } else {
                    setError("Unable to load this list.");
                }
            } finally {
                if (isActive) setIsLoading(false);
            }
        };

        fetchFirstPage();
        return () => { isActive = false; };
    }, [fetchPage]);

    if (isLoading) {
        return <p className={styles.message}>Loading...</p>;
    }

    if (error) {
        return <p className={styles.message}>{error}</p>;
    }

    const emptyMessage = isFollowersPage
        ? (isOwnProfile ? "You don't have any followers yet." : "This user doesn't have any followers yet.")
        : (isOwnProfile ? "You are not following anyone yet." : "This user isn't following anyone yet.");

    return (
        <div className={styles.followingContainer}>
            <h2 className={styles.title}>{isFollowersPage ? "Followers" : "Following"}</h2>

            {rows.length === 0 ? (
                <p className={styles.message}>{emptyMessage}</p>
            ) : (
                <>
                    <div className={styles.userList}>
                        {rows.map((follow) => {
                            const user = isFollowersPage
                                ? follow.follower
                                : follow.following;

                            if (!user) {
                                return null;
                            }

                            return (
                                <div key={follow._id} className={styles.userCard}>
                                    <div
                                        className={styles.userContent}
                                        onClick={() => navigate(`/account/${user._id}`)}
                                    >
                                        <img
                                            src={user.profilePicture || "/default-profile.png"}
                                            alt={user.username}
                                            className={styles.avatar}
                                        />

                                        <div className={styles.userInfo}>
                                            <h3 className={styles.username}>
                                                {user.username}
                                            </h3>

                                            <p className={styles.stats}>
                                                {user.followerCount || 0} Followers •{" "}
                                                {user.followingCount || 0} Following
                                            </p>
                                        </div>
                                    </div>

                                    {/* Only show Follow/Unfollow on your own follower/following list */}
                                    {isOwnProfile && user._id !== loggedInUser?._id && (
                                        <button
                                            className={styles.followButton}
                                            onClick={() =>
                                                handleFollowToggle(user._id, follow.isFollowing)
                                            }
                                            disabled={loadingUserId === user._id}
                                        >
                                            {loadingUserId === user._id
                                                ? "Loading..."
                                                : follow.isFollowing
                                                    ? "Following"
                                                    : "Follow"}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {hasMore && (
                        <button
                            className={styles.loadMoreButton}
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                        >
                            {isLoadingMore ? "Loading..." : "Load more"}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

export default FollowingPage;
